import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Slider, Space, Popconfirm, message } from 'antd';
import { 
  ArrowLeftOutlined, 
  CaretRightOutlined, 
  PauseOutlined, 
  SoundOutlined, 
  RedoOutlined, 
  FullscreenOutlined, 
  FullscreenExitOutlined,
  BackwardOutlined,
  ForwardOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { startVideoWatchTrack, pingVideoWatchTrack } from '../../../Service/VideoWatchTrack_API/videoWatchTrackApi';
import { getVideoUrl, SERVER_URL } from '../../../Service/apiConfig';
import { getVideoDownloadUrl } from '../../../Service/VideoIndex_API/videoIndexApi';
import { getEncryptedLiveStreamUrl } from '../../../Utils/videoEncryption';
import * as flvjsPkg from 'flv.js';

const getFlvjs = () => flvjsPkg.default || flvjsPkg || window.flvjs;

const VideoPlayerPage = ({ video, course, module, userRole, onBack }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const watchSecondsRef = useRef(0);
  const trackingIntervalRef = useRef(null);
  const watchTrackIdRef = useRef(null);

  // User details for watermark
  const authUser = useSelector((state) => state.auth?.user);
  const localUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      return null;
    }
  })();
  const userObj = authUser || localUser || {};
  const username = userObj.username || userObj.login_name || userObj.name || userObj.email || 'User';

  // Client IP & Timestamp states
  const [clientIp, setClientIp] = useState('127.0.0.1');

  const getFormattedTimestamp = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = now.getFullYear();
    const mm = pad(now.getMonth() + 1);
    const dd = pad(now.getDate());
    const hh = pad(now.getHours());
    const min = pad(now.getMinutes());
    const ss = pad(now.getSeconds());
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };

  const [watermarkTime, setWatermarkTime] = useState(getFormattedTimestamp());
  const [watermarkPos, setWatermarkPos] = useState({ top: 20, left: 20 });

  // Resolve client IP address
  useEffect(() => {
    let isMounted = true;
    const fetchIp = async () => {
      try {
        const cached = sessionStorage.getItem('user_client_ip');
        if (cached) {
          if (isMounted) setClientIp(cached);
          return;
        }
        const res = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          if (data?.ip) {
            if (isMounted) setClientIp(data.ip);
            sessionStorage.setItem('user_client_ip', data.ip);
            return;
          }
        }
      } catch (e) {
        // Continue to fallback
      }

      try {
        const res2 = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
        if (res2.ok) {
          const data2 = await res2.json();
          if (data2?.ip) {
            if (isMounted) setClientIp(data2.ip);
            sessionStorage.setItem('user_client_ip', data2.ip);
            return;
          }
        }
      } catch (e) {
        if (isMounted && window.location.hostname) {
          setClientIp(window.location.hostname);
        }
      }
    };

    fetchIp();
    return () => {
      isMounted = false;
    };
  }, []);

  // Update timestamp every 1 second, and reposition watermark every 4 seconds for a slower, calmer movement
  useEffect(() => {
    let tickCount = 0;
    const watermarkInterval = setInterval(() => {
      setWatermarkTime(getFormattedTimestamp());
      tickCount += 1;
      // Change position every 4 seconds
      if (tickCount % 4 === 0) {
        const nextTop = Math.floor(Math.random() * 76) + 6;
        const nextLeft = Math.floor(Math.random() * 57) + 5;
        setWatermarkPos({ top: nextTop, left: nextLeft });
      }
    }, 1000);

    return () => {
      clearInterval(watermarkInterval);
    };
  }, []);

  // States
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  // Hide controls after 3 seconds of inactivity
  const controlsTimeoutRef = useRef(null);
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  useEffect(() => {
    // Start interval tracking when playing (every 1 second)
    trackingIntervalRef.current = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        watchSecondsRef.current += 1;
        if (watchTrackIdRef.current) {
          pingVideoWatchTrack(watchTrackIdRef.current, watchSecondsRef.current);
        }
      }
    }, 1000);

    return () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
      }
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [video.video_id, isPlaying]);

  // Video Watch Tracking session (inserts into video_watch_track)
  useEffect(() => {
    if (isPlaying) {
      if (!watchTrackIdRef.current) {
        startVideoWatchTrack({
          courses_id: course?.courses_id || video?.courses_id || null,
          modules_id: module?.modules_id || video?.modules_id || null,
          video_id: video?.video_id || null,
          video_title: video?.video_title || 'Untitled Video',
          video_type: 'VOD',
          video_duration: watchSecondsRef.current
        }).then(res => {
          if (res && res.video_watch_track_id) {
            watchTrackIdRef.current = res.video_watch_track_id;
          }
        }).catch(e => console.warn('Watch track start failure:', e));
      }
    } else {
      if (watchTrackIdRef.current) {
        pingVideoWatchTrack(watchTrackIdRef.current, watchSecondsRef.current);
      }
    }

    return () => {
      if (watchTrackIdRef.current) {
        pingVideoWatchTrack(watchTrackIdRef.current, watchSecondsRef.current);
      }
    };
  }, [isPlaying, video, course, module]);

  const isLiveStreamVideo = Boolean(
    video?.rtmp_stream_key &&
      (video?.rtmp_status === 'LIVE' ||
        video?.video_file_name === 'raw_live_stream.mp4' ||
        video?.video_file_name === 'processing.mp4')
  );
  const flvPlayerRef = useRef(null);

  useEffect(() => {
    if (!isLiveStreamVideo || !videoRef.current) return undefined;

    let isMounted = true;
    let retryTimer = null;

    const cleanupFlv = () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (flvPlayerRef.current) {
        try {
          flvPlayerRef.current.pause();
          flvPlayerRef.current.unload();
          flvPlayerRef.current.detachMediaElement();
          flvPlayerRef.current.destroy();
        } catch (e) {}
        flvPlayerRef.current = null;
      }
    };

    const connectLiveFlv = () => {
      if (!isMounted || !videoRef.current) return;
      cleanupFlv();
      const flv = getFlvjs();
      if (flv && flv.isSupported && flv.isSupported()) {
        try {
          const flvUrl = getEncryptedLiveStreamUrl(video.rtmp_stream_key, SERVER_URL);
          const flvPlayer = flv.createPlayer(
            {
              type: 'flv',
              url: flvUrl,
              isLive: true,
              cors: true
            },
            {
              enableWorker: false,
              enableStashBuffer: false,
              stashInitialSize: 128,
              isLive: true,
              lazyLoad: false,
              autoCleanupSourceBuffer: true
            }
          );

          if (flv.Events && flv.Events.ERROR) {
            flvPlayer.on(flv.Events.ERROR, () => {
              if (!isMounted) return;
              cleanupFlv();
              retryTimer = setTimeout(() => {
                if (isMounted) connectLiveFlv();
              }, 1500);
            });
          }

          flvPlayer.attachMediaElement(videoRef.current);
          flvPlayer.load();
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise
              .then(() => {
                if (isMounted) setIsPlaying(true);
              })
              .catch(() => {
                if (!isMounted || !videoRef.current) return;
                videoRef.current.muted = true;
                setIsMuted(true);
                videoRef.current
                  .play()
                  .then(() => {
                    if (isMounted) setIsPlaying(true);
                  })
                  .catch(() => {});
              });
          }
          flvPlayerRef.current = flvPlayer;
        } catch (e) {
          retryTimer = setTimeout(() => {
            if (isMounted) connectLiveFlv();
          }, 1500);
        }
      }
    };

    connectLiveFlv();

    return () => {
      isMounted = false;
      cleanupFlv();
    };
  }, [isLiveStreamVideo, video?.rtmp_stream_key]);

  // Video source helper
  const getVideoSrc = () => {
    if (isLiveStreamVideo) return undefined;
    return getVideoUrl(video);
  };

  // Download video handler for ADMIN
  const handleDownloadVideo = () => {
    if (userRole !== 'ADMIN') return;
    if (!video || !video.video_id) return;
    if (!video.video_file_name || video.video_file_name === 'processing.mp4') {
      message.warning('This video is currently processing or has no media file available yet.');
      return;
    }
    const downloadUrl = getVideoDownloadUrl(video.video_id);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.setAttribute('download', '');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    message.success(`Starting download for "${video.video_title || 'video'}"...`);
  };

  const handlePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      const playPromise = v.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          setIsPlaying(true);
        }).catch(err => {
          if (err.name !== 'AbortError') {
            console.warn('Playback error:', err.message);
          }
        });
      }
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const handleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleVolumeChange = (val) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = val;
    v.muted = val === 0;
    setVolume(val);
    setIsMuted(val === 0);
  };

  const handleSeek = (val) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = val;
    setCurrentTime(val);
  };

  const handleBackward = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, v.currentTime - 10);
    setCurrentTime(v.currentTime);
  };

  const handleForward = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(v.duration, v.currentTime + 10);
    setCurrentTime(v.currentTime);
  };

  const handleRestart = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    const playPromise = v.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        setIsPlaying(true);
      }).catch(err => {
        if (err.name !== 'AbortError') {
          console.warn('Restart playback error:', err.message);
        }
      });
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Error enabling fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Monitor fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard hotkeys for player controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
        return;
      }
      const v = videoRef.current;
      if (!v) return;

      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        handlePlayPause();
      } else if (e.key === 'ArrowLeft' || e.key === 'j') {
        e.preventDefault();
        handleBackward();
      } else if (e.key === 'ArrowRight' || e.key === 'l') {
        e.preventDefault();
        handleForward();
      } else if (e.key === 'f') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPlaying, isFullscreen]);

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '00:00:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle video events
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      <style>{`
        .custom-video-container {
          position: relative;
          background: #000;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
          border: 1px solid var(--border-color);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 520px;
          min-height: 360px;
        }
        @media (max-width: 768px) {
          .custom-video-container {
            height: 320px;
          }
        }
        .custom-video-container:fullscreen,
        .custom-video-container:-webkit-full-screen,
        .custom-video-container:-moz-full-screen,
        .custom-video-container:-ms-fullscreen {
          width: 100vw !important;
          height: 100vh !important;
          max-width: 100vw !important;
          max-height: 100vh !important;
          border-radius: 0 !important;
          border: none !important;
          background: #000 !important;
        }
        .video-element {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
        }
        .custom-video-container:fullscreen .video-element,
        .custom-video-container:-webkit-full-screen .video-element,
        .custom-video-container:-moz-full-screen .video-element,
        .custom-video-container:-ms-fullscreen .video-element,
        video:fullscreen,
        video:-webkit-full-screen,
        video:-moz-full-screen,
        video:-ms-fullscreen {
          max-height: 100vh !important;
          height: 100vh !important;
          width: 100vw !important;
          object-fit: contain !important;
        }
        .video-controls-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.9));
          padding: 24px 16px 16px 16px;
          opacity: 0;
          transition: opacity 0.3s ease;
          display: flex;
          flex-direction: column;
          gap: 12px;
          pointer-events: none;
          z-index: 10;
        }
        .video-controls-overlay.show {
          opacity: 1;
          pointer-events: auto;
        }
        .video-top-title {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          background: linear-gradient(rgba(0,0,0,0.8), transparent);
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          z-index: 10;
        }
        .video-controls-overlay.show ~ .video-top-title,
        .video-top-title.show {
          opacity: 1;
          pointer-events: auto;
        }
        .control-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .video-btn {
          color: var(--text-color);
          background: transparent !important;
          border-color: rgba(255,255,255,0.2) !important;
        }
        .video-btn:hover {
          color: #fff !important;
          border-color: #fff !important;
        }
        .progress-bar-container {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
        }
        video::-internal-media-controls-download-button {
          display: none !important;
        }
        video::-webkit-media-controls-download-button {
          display: none !important;
        }
        video::-webkit-media-controls-enclosure {
          overflow: hidden;
        }
        .dynamic-video-watermark {
          position: absolute;
          pointer-events: none;
          user-select: none;
          z-index: 8;
          opacity: 0.2;
          color: #ffffff;
          text-shadow: 0 1px 2px rgba(0,0,0,0.85);
          font-size: 11px;
          line-height: 1.35;
          font-weight: 500;
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
          letter-spacing: 0.3px;
          white-space: nowrap;
          background: transparent;
          border: none;
          padding: 0;
          display: flex;
          flex-direction: column;
        }
        @media (max-width: 768px) {
          .dynamic-video-watermark {
            font-size: 9px;
            line-height: 1.25;
          }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back to Videos
        </Button>
        <h3 style={{ color: 'var(--text-color)', margin: 0 }}>Playing: {video.video_title}</h3>
        {userRole === 'ADMIN' ? (
          <Popconfirm
            title="Download Video"
            description={`Are you sure you want to download "${video?.video_title || 'this video'}"?`}
            onConfirm={handleDownloadVideo}
            okText="Download"
            cancelText="Cancel"
          >
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              style={{ borderRadius: 6, background: '#10b981', borderColor: '#10b981' }}
            >
              Download Video
            </Button>
          </Popconfirm>
        ) : (
          <div style={{ width: 110 }} />
        )}
      </div>

      {/* Video Container */}
      <div 
        ref={containerRef}
        className="custom-video-container"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setShowControls(false)}
        onContextMenu={(e) => e.preventDefault()}
      >
        <video
          ref={videoRef}
          className="video-element"
          src={getVideoSrc()}
          preload="auto"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onClick={handlePlayPause}
          controlsList="nodownload"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* Dynamic Moving Watermark Overlay (Anti-Piracy & Screencast Deterrence) */}
        <div 
          className="dynamic-video-watermark"
          style={{
            top: `${watermarkPos.top}%`,
            left: `${watermarkPos.left}%`,
          }}
        >
          <div>{username}</div>
          <div>{watermarkTime}</div>
          <div>{clientIp}</div>
        </div>

        {/* CENTERED PLAY BUTTON OVERLAY WHEN PAUSED */}
        {!isPlaying && (
          <div
            onClick={handlePlayPause}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(99, 102, 241, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              zIndex: 15,
              transition: 'transform 0.2s ease, background 0.2s ease'
            }}
          >
            <CaretRightOutlined style={{ fontSize: 36, color: '#fff', marginLeft: 4 }} />
          </div>
        )}

        {/* TOP TITLE & FULLSCREEN OVERLAY */}
        <div className={`video-top-title ${showControls ? 'show' : ''}`}>
          <span style={{ color: 'var(--text-color)', fontSize: 16, fontWeight: 600 }}>{video.video_title}</span>
          <Button 
            className="video-btn"
            size="small"
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
          />
        </div>

        {/* CONTROLS OVERLAY */}
        <div className={`video-controls-overlay ${showControls ? 'show' : ''}`}>
          {/* Progress bar */}
          <div className="progress-bar-container">
            <span className="time-label">{formatTime(currentTime)}</span>
            <Slider
              min={0}
              max={duration || 100}
              step={0.1}
              value={currentTime}
              onChange={handleSeek}
              style={{ flex: 1, margin: 0 }}
              tooltip={{ formatter: formatTime }}
            />
            <span className="time-label">{formatTime(duration)}</span>
          </div>

          {/* Buttons Row */}
          <div className="control-row">
            <Space size="middle">
              <Button
                className="video-btn"
                shape="circle"
                icon={<BackwardOutlined />}
                onClick={handleBackward}
                title="Rewind 10s"
              />

              <Button
                className="video-btn"
                shape="circle"
                icon={isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
                onClick={handlePlayPause}
              />

              <Button
                className="video-btn"
                shape="circle"
                icon={<ForwardOutlined />}
                onClick={handleForward}
                title="Forward 10s"
              />
              
              <Button
                className="video-btn"
                shape="circle"
                icon={<RedoOutlined />}
                onClick={handleRestart}
              />

              <Space style={{ marginLeft: 8 }}>
                <Button
                  className="video-btn"
                  shape="circle"
                  icon={isMuted ? <span style={{ textDecoration: 'line-through' }}><SoundOutlined /></span> : <SoundOutlined />}
                  onClick={handleMute}
                />
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  style={{ width: 80, margin: 0 }}
                />
              </Space>
            </Space>

            <Button 
              className="video-btn"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerPage;
